import { useEffect } from "react";
import { Navigate, Outlet, useNavigate, useOutletContext } from "react-router";

import { Button, ButtonStyle, PageHeading, Spinner } from "@prequel/shared-components";

import type { DestinationResource } from "@/api/import/destinations";
import { useDestination } from "@/api/import/destinations";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";
import { useToast } from "@/providers/ToastProvider";

export type DestinationContext = {
  destination: DestinationResource;
};

export function useDestinationContext(): DestinationContext {
  return useOutletContext<DestinationContext>();
}

const DestinationPage = () => {
  const destinationID = useRequiredParam("destinationID");
  const { addToast } = useToast();
  const { data: destination, error, isLoading } = useDestination(destinationID);

  useEffect(() => {
    if (error) {
      addToast({ kind: "ERROR", message: "Failed to load destination." });
    }
  }, [error, addToast]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!destination) {
    return <Navigate to="/import/destinations" replace />;
  }

  return <Outlet context={{ destination }} />;
};

export const DestinationDetailsPage = () => {
  const navigate = useNavigate();
  const { destination } = useDestinationContext();

  return (
    <div className="max-w-4xl px-7 py-4">
      <PageHeading
        title={destination.name}
        onGoBack={() => navigate("/import/destinations")}
        breadcrumbs={<Breadcrumbs dynamicLabel={destination.name} />}
      >
        <Button
          text="Edit"
          onClick={() => navigate(`/import/destinations/${destination.id}/edit`)}
          type={ButtonStyle.SECONDARY}
        />
      </PageHeading>
      <div className="flex-1">
        <div className="relative mx-auto max-w-4xl">
          <div className="pt-0 pb-16">
            <Outlet context={{ destination }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DestinationPage;
